import { ChangeDetectorRef, OnDestroy, Pipe, PipeTransform } from '@angular/core';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { PreferencesService } from '../services/preferences.service';
import {
  DateFormat,
  TimeFormat,
  TimePrecision,
  formatDateTime,
  normalizeDateFormat,
  normalizeTimeFormat,
} from './date-format';

/**
 * `{{ someDate | vcDate }}` / `{{ someDate | vcDate: 'seconds' }}`
 *
 * Replaces Angular's `date` pipe wherever a date is shown to a user, because that
 * pipe takes a FIXED pattern in the template — "M/d/yy, h:mm:ss a" was written
 * into six templates — and a fixed pattern cannot follow a preference.
 *
 * Pure, for the same reason the translate pipe is: it watches the resolved date
 * format and marks its own view when that actually changes, instead of
 * reformatting every date on every change-detection pass.
 */
@Pipe({ name: 'vcDate', pure: true })
export class VcDatePipe implements PipeTransform, OnDestroy {
  private format: DateFormat;
  private timeFormat: TimeFormat;
  private sub: Subscription;

  constructor(private preferences: PreferencesService, private cdr: ChangeDetectorRef) {
    const initial = this.preferences.effective$.value;
    this.format = normalizeDateFormat(initial.dateFormat);
    this.timeFormat = normalizeTimeFormat(initial.timeFormat);
    // BOTH settings, one subscription. Watching them separately would re-render
    // twice when a user changes both, and — worse — the two could briefly
    // disagree, showing a new date order against the old clock.
    this.sub = this.preferences.effective$
      .pipe(
        map((prefs) => ({
          date: normalizeDateFormat(prefs.dateFormat),
          time: normalizeTimeFormat(prefs.timeFormat),
        })),
        distinctUntilChanged((a, b) => a.date === b.date && a.time === b.time)
      )
      .subscribe(({ date, time }) => {
        const changed = date !== this.format || time !== this.timeFormat;
        this.format = date;
        this.timeFormat = time;
        if (changed) this.cdr.markForCheck();
      });
  }

  transform(value: Date | string | number | null | undefined, precision: TimePrecision = 'none'): string {
    return formatDateTime(value, this.format, precision, this.timeFormat);
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
